<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<!-- Compact "current selection" annotation panel (design: ims-annotation-panel).
     A pinned metric strip (area / perimeter / length — adapts to the annotation's
     geometry type), an always-visible Terms block, a chip rail whose sections open
     one at a time below (collapsed by default), and a pinned action footer. All the
     original sub-components and behaviours are preserved. -->
<template>
<div class="annotation-details">

  <!-- pinned metrics (geometry-type aware) + profile inspectors -->
  <div v-if="isPropDisplayed('geometry-info') && (hasMetrics || profile)" class="ap-metrics">
    <div v-if="hasMetrics" class="ap-metric-row">
      <template v-for="(m, i) in metrics">
        <div v-if="i > 0" :key="m.key + '-sep'" class="ap-metric-sep"></div>
        <div :key="m.key" class="ap-metric">
          <div class="ap-metric-label">{{ $t(m.key) }}</div>
          <div class="ap-metric-val">
            <span class="num">{{ m.value }}</span>
            <span class="unit">{{ m.unit }}</span>
          </div>
        </div>
      </template>
    </div>
    <!-- rectangles also get width × height (own row); click to switch px ↔ µm/mm -->
    <div v-if="rectMetrics.length" class="ap-metric-row ap-metric-row-rect"
         :class="{clickable: canToggleRectUnit}"
         :title="canToggleRectUnit ? $t('click-to-switch-unit') : ''"
         @click="toggleRectUnit">
      <template v-for="(m, i) in rectMetrics">
        <div v-if="i > 0" :key="m.key + '-sep'" class="ap-metric-sep"></div>
        <div :key="m.key" class="ap-metric">
          <div class="ap-metric-label">{{ $t(m.key) }}</div>
          <div class="ap-metric-val">
            <span class="num">{{ m.value }}</span>
            <span class="unit">{{ m.unit }}</span>
          </div>
        </div>
      </template>
    </div>
    <div v-if="profile" class="ap-profile">
      <button class="ap-mini-btn" @click="openRegularProfileModal">
        {{ isPoint ? $t('profile') : $t('profile-projection') }} · {{ $t('inspect-button') }}
      </button>
      <button v-if="!isPoint" class="ap-mini-btn" @click="openSpatialProfileModal">
        {{ spatialProjection }} · {{ $t('inspect-button') }}
      </button>
    </div>
  </div>

  <!-- pinned terms (always visible) -->
  <div v-if="isPropDisplayed('terms') && ontology" class="ap-terms" v-click-outside="() => showTermSelector = false">
    <div class="ap-terms-row">
      <span class="ap-mini-label">{{ $t('terms') }}</span>
      <span v-for="{term, user} in associatedTerms" :key="term.id" class="ap-term-chip"
            :title="$t('associated-by', {username: user.name})">
        <ims-term :term="term" />
        <button v-if="canEditTerms" class="ap-chip-x" :title="$t('button-delete')"
                @click="removeTerm(term.id, user.id)">
          <svg width="9" height="9" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
      </span>
      <button v-if="canEditTerms" class="ap-term-add" :title="$t('add-term')"
              @click="showTermSelector = !showTermSelector">
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
      </button>
      <em v-else-if="!associatedTerms.length" class="ap-empty">{{ $t('no-term') }}</em>
    </div>
    <div v-if="canEditTerms && showTermSelector" class="add-term-wrapper">
      <b-field>
        <b-input size="is-small" expanded :placeholder="$t('add-term')" v-model="addTermString" />
      </b-field>
      <div class="ontology-tree-container">
        <ontology-tree
          class="ontology-tree"
          :ontology="ontology"
          :searchString="addTermString"
          :selectedNodes="associatedTermsIds"
          :allowNew="true"
          @newTerm="newTerm"
          @select="addTerm"
          @unselect="removeTerm"
        />
      </div>
    </div>
  </div>

  <!-- pinned section chip rail -->
  <div class="ap-rail">
    <button v-if="isPropDisplayed('description')" class="ap-chip" :class="{on: active === 'description'}" @click="toggleSection('description')">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M3 8h10M3 12h6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
      {{ $t('description') }}
    </button>
    <button v-if="isPropDisplayed('tags')" class="ap-chip" :class="{on: active === 'tags'}" @click="toggleSection('tags')">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M7.5 2.5l5.5.5.5 5.5-6 6-5.5-5.5z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="9.7" cy="6.3" r="1" fill="currentColor"/></svg>
      {{ $t('tags') }}
    </button>
    <button v-if="isPropDisplayed('properties')" class="ap-chip" :class="{on: active === 'properties'}" @click="toggleSection('properties')">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M6 4h8M6 8h8M6 12h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="2.7" cy="4" r="1.1" fill="currentColor"/><circle cx="2.7" cy="8" r="1.1" fill="currentColor"/><circle cx="2.7" cy="12" r="1.1" fill="currentColor"/></svg>
      {{ $t('properties') }}<span class="ap-count">{{ propCount }}</span>
    </button>
    <button v-if="isPropDisplayed('attached-files')" class="ap-chip" :class="{on: active === 'files'}" @click="toggleSection('files')">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M10 5L5.5 9.5a2 2 0 1 0 2.8 2.8l4.2-4.2a3.4 3.4 0 0 0-4.8-4.8L3.6 7.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      {{ $t('files') }}
    </button>
    <button v-if="hasTracksSection" class="ap-chip" :class="{on: active === 'tracks'}" @click="toggleSection('tracks')">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="4" cy="12" r="2" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="4" r="2" stroke="currentColor" stroke-width="1.5"/><path d="M6 11l4-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      {{ $t('tracks') }}<span class="ap-count">{{ associatedTracks.length }}</span>
    </button>
    <button class="ap-chip" :class="{on: active === 'similar'}" @click="toggleSection('similar')">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.2" stroke="currentColor" stroke-width="1.6"/><path d="M10.4 10.4L14 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
      {{ $t('similar') }}
    </button>
    <button v-if="isPropDisplayed('linked-annotations')" class="ap-chip" :class="{on: active === 'linked'}" @click="toggleSection('linked')">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M6.5 9.5l3-3M5.5 6.5L4 8a2.4 2.4 0 0 0 3.4 3.4L9 9.8M10.5 9.5L12 8a2.4 2.4 0 0 0-3.4-3.4L7 6.2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      {{ $t('linked') }}
    </button>
    <button v-if="showInfoSection" class="ap-chip" :class="{on: active === 'info'}" @click="toggleSection('info')">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/><path d="M8 7.3v3.4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="8" cy="5.2" r="0.9" fill="currentColor"/></svg>
      {{ $t('info') }}
    </button>
  </div>

  <!-- detail region (one section at a time) -->
  <div v-show="active" class="ap-detail">
    <div class="ap-detail-head">
      <span class="ap-detail-label">{{ activeLabel }}</span>
      <span class="ap-detail-line"></span>
      <button class="ap-collapse" @click="active = null">
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
    <div class="ap-detail-body">

      <div v-show="active === 'description'">
        <ims-description :object="annotation" :canEdit="canEdit" :maxPreviewLength="300" />
      </div>

      <div v-show="active === 'tags'">
        <ims-tags :object="annotation" :canEdit="canEdit" />
      </div>

      <div v-show="active === 'properties'">
        <ims-properties :object="annotation" :canEdit="canEdit" @update="$emit('updateProperties')" />
      </div>

      <div v-show="active === 'files'">
        <attached-files :object="annotation" :canEdit="canEdit" />
      </div>

      <div v-if="hasTracksSection" v-show="active === 'tracks'">
        <b-tag v-for="{track} in associatedTracks" :key="track.id">
          <ims-track :track="track" />
          <button v-if="canEditTerms" class="delete is-small" :title="$t('button-delete')" @click="removeTrack(track.id)"></button>
        </b-tag>
        <div class="add-track-wrapper" v-if="canEditTerms" v-click-outside="() => showTrackSelector = false">
          <b-field>
            <b-input size="is-small" expanded :placeholder="$t('add-track')" v-model="addTrackString" @focus="showTrackSelector = true" />
          </b-field>
          <div class="track-tree-container" v-show="showTrackSelector">
            <track-tree
              class="track-tree"
              :tracks="availableTracks"
              :searchString="addTrackString"
              :selectedNodes="associatedTracksIds"
              :allowNew="true"
              :image="image"
              @newTrack="newTrack"
              @select="addTrack"
              @unselect="removeTrack"
            />
          </div>
        </div>
        <em v-else-if="!associatedTracks.length" class="ap-empty">{{ $t('no-track') }}</em>
      </div>

      <div v-show="active === 'similar'">
        <button class="ap-search-btn" @click="$emit('searchSimilarAnnotations')">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.8"/><path d="M10.5 10.5L14 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          {{ $t('search-similar-annotation') }}
        </button>
      </div>

      <div v-if="isPropDisplayed('linked-annotations')" v-show="active === 'linked'">
        <annotation-links-preview
          :size="linkCropSize"
          :link-color="linkColor"
          :show-main-annotation="false"
          :show-select-all-button="!showImageInfo"
          :allow-annotation-selection="true"
          :annotation="annotation"
          :images="images"
          @select="$emit('select', $event)"
        />
      </div>

      <div v-if="showInfoSection" v-show="active === 'info'" class="ap-info">
        <div v-if="showImageInfo" class="ap-info-row">
          <span class="k">{{ $t('image') }}</span>
          <router-link class="v" :to="`/project/${annotation.project}/image/${annotation.image}`">
            <image-name :image="image" />
          </router-link>
        </div>
        <div v-if="showChannelInfo" class="ap-info-row">
          <span class="k">{{ $t('channel') }}</span>
          <span class="v"><channel-name :channel="sliceChannel" /></span>
        </div>
        <template v-if="isPropDisplayed('creation-info')">
          <div class="ap-info-row">
            <span class="k">{{ $t('created-by') }}</span>
            <span class="v">{{ creator.name }}</span>
          </div>
          <template v-if="!isReviewedAnnotation">
            <div class="ap-info-row">
              <span class="k">{{ $t('created-on') }}</span>
              <span class="v">{{ Number(annotation.created) | moment('ll') }}</span>
            </div>
            <div v-if="isImageInReviewMode" class="ap-info-row">
              <span class="k">{{ $t('reviewed-annotation-status') }}</span>
              <span class="v"><span class="tag is-danger">{{ $t('reviewed-annotation-status-not-validated') }}</span></span>
            </div>
          </template>
          <template v-else>
            <div class="ap-info-row">
              <span class="k">{{ $t('reviewed-by') }}</span>
              <span class="v">{{ reviewer.name }}</span>
            </div>
            <div class="ap-info-row">
              <span class="k">{{ $t('reviewed-on') }}</span>
              <span class="v">{{ Number(annotation.created) | moment('ll') }}</span>
            </div>
            <div v-if="isImageInReviewMode" class="ap-info-row">
              <span class="k">{{ $t('reviewed-annotation-status') }}</span>
              <span class="v"><span class="tag is-success">{{ $t('reviewed-annotation-status-validated') }}</span></span>
            </div>
          </template>
        </template>
        <div v-if="currentAccount.isDeveloper" class="ap-info-row">
          <span class="k">{{ $t('id') }}</span>
          <span class="v mono">{{ annotation.id }}</span>
        </div>
      </div>

    </div>
  </div>

  <!-- pinned footer / actions -->
  <div class="ap-footer">
    <router-link v-if="showImageInfo" :to="annotationURL" class="ap-primary" :title="$t('button-view-in-image')">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2.1" fill="currentColor"/><circle cx="8" cy="8" r="5.2" stroke="currentColor" stroke-width="1.5"/><path d="M8 0.6v2.2M8 13.2v2.2M0.6 8h2.2M13.2 8h2.2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
      {{ $t('button-view-in-image') }}
    </router-link>
    <a v-else class="ap-primary" :title="$t('button-center-view-on-annot')" @click="$emit('centerView')">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2.1" fill="currentColor"/><circle cx="8" cy="8" r="5.2" stroke="currentColor" stroke-width="1.5"/><path d="M8 0.6v2.2M8 13.2v2.2M0.6 8h2.2M13.2 8h2.2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
      {{ $t('center') }}
    </a>

    <button class="ap-ico" :title="$t('button-view-crop')" @click="openCrop(annotation)">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M5 2v9a1 1 0 0 0 1 1h9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M11 14V5a1 1 0 0 0-1-1H1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
    </button>

    <button class="ap-ico" :title="$t('button-copy-url')" @click="copyURL()">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" stroke-width="1.5"/><path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2H3.5A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" stroke="currentColor" stroke-width="1.5"/></svg>
    </button>

    <!-- Zaya Chat "Share in chat" (AC-12.4) — opens the chat share picker via the app's existing
         global $eventBus (see src/chat/shareBus.js), rather than this panel importing chat
         internals directly. -->
    <button class="ap-ico" :title="$t('button-share-in-chat') || 'Share in chat'" @click="shareInChat()">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M11 5.5a2 2 0 1 0-1.8-2.9M11 10.5a2 2 0 1 0-1.8 2.9M5 8a2 2 0 1 0-2 2m2-2a2 2 0 1 0-2-2m2 2l4-2.3M5 8l4 2.3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>

    <button v-if="isPropDisplayed('comments') && comments" class="ap-ico ap-ico-wide" :title="$t('button-comments')" @click="openCommentsModal()">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 4.5A1.5 1.5 0 0 1 3.5 3h9A1.5 1.5 0 0 1 14 4.5v5A1.5 1.5 0 0 1 12.5 11H7l-3.5 3V11H3.5A1.5 1.5 0 0 1 2 9.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>
      <span class="num">{{ comments.length }}</span>
    </button>

    <button v-if="canEdit" class="ap-ico ap-ico-danger" :title="$t('button-delete')" @click="confirmDeletion()">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M6.5 4V2.8A.8.8 0 0 1 7.3 2h1.4a.8.8 0 0 1 .8.8V4M4.5 4l.5 8.5a1 1 0 0 0 1 .95h4a1 1 0 0 0 1-.95L12 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </button>
  </div>
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';

import {AnnotationTerm, AnnotationType, AnnotationCommentCollection, AnnotationTrack, PropertyCollection} from '@/api';
import copyToClipboard from 'copy-to-clipboard';
import ImageName from '@/components/image/ImageName';
import ImsDescription from '@/components/description/ImsDescription';
import ImsProperties from '@/components/property/ImsProperties';
import ImsTags from '@/components/tag/ImsTags';
import ImsTerm from '@/components/ontology/ImsTerm';
import AttachedFiles from '@/components/attached-file/AttachedFiles';
import OntologyTree from '@/components/ontology/OntologyTree';
import TrackTree from '@/components/track/TrackTree';
import ImsTrack from '@/components/track/ImsTrack';
import AnnotationCommentsModal from './AnnotationCommentsModal';
import ProfileModal from '@/components/viewer/ProfileModal';
import AnnotationLinksPreview from '@/components/annotations/AnnotationLinksPreview';
import {appendShortTermToken} from '@/utils/token-utils.js';
import ChannelName from '@/components/viewer/ChannelName';
import constants from '@/utils/constants.js';
import {geometryTypeKey, wktBBoxSize} from '@/utils/geometry-utils';
import {shareInChat} from '@/chat/shareBus.js';

export default {
  name: 'annotations-details',
  components: {
    ChannelName,
    ImageName,
    ImsDescription,
    ImsTerm,
    OntologyTree,
    ImsTags,
    ImsProperties,
    AttachedFiles,
    TrackTree,
    ImsTrack,
    AnnotationLinksPreview,
  },
  props: {
    annotation: {type: Object},
    terms: {type: Array},
    tracks: {type: Array},
    users: {type: Array},
    images: {type: Array},
    slices: {type: Array, default: () => []},
    profiles: {type: Array, default: () => []},
    showImageInfo: {type: Boolean, default: true},
    showChannelInfo: {type: Boolean, default: false},
    showComments: {type: Boolean, default: false}
  },
  data() {
    return {
      active: null, // chip-rail: the single open section (null = collapsed, the default)
      addTermString: '',
      addTrackString: '',
      showTermSelector: false,
      showTrackSelector: false,
      comments: null,
      revTerms: 0,
      revTracks: 0,
      linkCropSize: 64,
      linkColor: '696969',
      properties: [],
      loadPropertiesError: false,
      rectUnitMode: 'px' // rectangle width/height unit: 'px' (default) | 'metric' (µm/mm)
    };
  },
  computed: {
    configUI: get('currentProject/configUI'),
    ontology: get('currentProject/ontology'),
    currentAccount: get('currentUser/account'),
    shortTermToken: get('currentUser/shortTermToken'),
    creator() {
      return this.users.find(user => user.id === this.annotation.user) || {};
    },
    isReviewedAnnotation() {
      return this.annotation.type === AnnotationType.REVIEWED;
    },
    reviewer() {
      if (this.isReviewedAnnotation) {
        return this.users.find(user => user.id === this.annotation.reviewUser) || {};
      }
      return null;
    },
    canEdit() {
      return this.$store.getters['currentProject/canEditAnnot'](this.annotation);
    },
    canEditTerms() {
      // HACK: because core prevents from modifying term of algo annot (https://github.com/cytomine/Ims-core/issues/1138 & 1139)
      // + term modification forbidden for reviewed annotation
      return this.canEdit && this.annotation.type === AnnotationType.USER;
    },
    image() {
      return this.images.find(image => image.id === this.annotation.image) ||
        {'id': this.annotation.image, 'instanceFilename': this.annotation.instanceFilename};
    },
    isImageInReviewMode() {
      return this.image.inReview;
    },
    sliceChannel() {
      return this.slices.find(slice => slice.id === this.annotation.slice) || {};
    },
    maxRank() {
      return this.image.depth * this.image.duration * this.image.channels;
    },
    profile() {
      return this.profiles.find(profile => profile.image === this.image.baseImage);
    },
    annotationURL() {
      return `/project/${this.annotation.project}/image/${this.annotation.image}/annotation/${this.annotation.id}`;
    },
    // Pinned metrics, adapted to the geometry: a polygon/rectangle/circle has an area +
    // perimeter, a line has only a length, a point has neither (strip hidden).
    metrics() {
      const list = [];
      const a = this.annotation;
      if (a.area > 0) {
        list.push({key: 'area', value: a.area.toFixed(3), unit: a.areaUnit});
      }
      if (a.perimeter > 0) {
        list.push({key: a.area > 0 ? 'perimeter' : 'length', value: a.perimeter.toFixed(3), unit: a.perimeterUnit});
      }
      return list;
    },
    hasMetrics() {
      return this.metrics.length > 0;
    },
    isRectangle() {
      return geometryTypeKey(this.annotation.location) === 'rectangle';
    },
    // a metric (µm/mm) value is only available when the image is calibrated
    canToggleRectUnit() {
      return this.image.physicalSizeX > 0 && this.image.physicalSizeY > 0;
    },
    // Width/height shown only for (axis-aligned) rectangles, derived from the WKT bbox.
    // Defaults to pixels; click the row to switch to the calibrated unit (µm/mm).
    rectMetrics() {
      if (!this.isRectangle) {
        return [];
      }
      let size = wktBBoxSize(this.annotation.location);
      if (!size) {
        return [];
      }
      let w = this.formatLength(size.width, this.image.physicalSizeX);
      let h = this.formatLength(size.height, this.image.physicalSizeY);
      return [
        {key: 'width', value: w.value, unit: w.unit},
        {key: 'height', value: h.value, unit: h.unit}
      ];
    },
    propCount() {
      return this.internalUseFilteredProperties.length;
    },
    hasTracksSection() {
      return this.isPropDisplayed('tracks') && this.maxRank > 1;
    },
    showInfoSection() {
      return this.isPropDisplayed('creation-info') || this.currentAccount.isDeveloper
        || this.showImageInfo || this.showChannelInfo;
    },
    activeLabel() {
      const map = {
        description: 'description', tags: 'tags', properties: 'properties',
        files: 'attached-files', tracks: 'tracks', similar: 'similar-annotations',
        linked: 'linked-annotations', info: 'creation-info'
      };
      return this.active ? this.$t(map[this.active]) : '';
    },
    associatedTerms() {
      if (this.annotation.userByTerm) {
        return this.annotation.userByTerm.map(ubt => {
          let term = this.terms.find(term => ubt.term === term.id);
          let user = this.users.find(user => user.id === ubt.user[0]) || {}; // QUESTION: can we have several users?
          return {term, user};
        }).filter(({term}) => term); // drop terms removed from the ontology (avoids an empty/broken panel)
      } else {
        return [];
      }
    },
    associatedTermsIds() {
      this.revTerms;
      return this.associatedTerms.map(({term}) => term.id);
    },
    associatedTracks() {
      if (this.annotation.annotationTrack) {
        return this.annotation.annotationTrack.map(at => {
          let track = this.tracks.find(track => at.track === track.id);
          return {track};
        }).filter(({track}) => track); // drop tracks removed since (avoids an empty/broken panel)
      } else {
        return [];
      }
    },
    associatedTracksIds() {
      this.revTracks;
      return this.associatedTracks.map(({track}) => track.id);
    },
    availableTracks() {
      return this.tracks.filter(track => track.image === this.annotation.image);
    },
    isPoint() {
      return this.annotation.location && this.annotation.location.includes('POINT');
    },
    internalUseFilteredProperties() {
      return this.properties.filter(prop => !prop.key.startsWith(constants.PREFIX_HIDDEN_PROPERTY_KEY));
    },
    spatialProjection() {
      if (this.image.channels > 1) {
        return this.$t('fluorescence-spectra');
      } else if (this.image.depth > 1) {
        return this.$t('depth-spectra');
      } else if (this.image.duration > 1) {
        return this.$t('temporal-spectra');
      }
      return  this.$t('spatial-projection');
    }
  },
  methods: {
    appendShortTermToken,
    isPropDisplayed(prop) {
      return this.configUI[`project-explore-annotation-${prop}`];
    },
    // px → display length. Pixels by default; in 'metric' mode (and when the image is
    // calibrated) converts to µm, or mm for the perimeter's unit.
    formatLength(px, perPixel) {
      if (this.rectUnitMode === 'metric' && perPixel && perPixel > 0) {
        let microns = px * perPixel;
        if (this.annotation.perimeterUnit === 'mm') {
          return {value: (microns / 1000).toFixed(3), unit: 'mm'};
        }
        return {value: microns.toFixed(2), unit: 'µm'};
      }
      return {value: px.toFixed(1), unit: 'px'};
    },
    // toggle the rectangle width/height between pixels and the calibrated unit
    toggleRectUnit() {
      if (this.canToggleRectUnit) {
        this.rectUnitMode = this.rectUnitMode === 'px' ? 'metric' : 'px';
      }
    },
    toggleSection(key) {
      this.active = (this.active === key) ? null : key;
    },
    openCrop(annotation) {
      window.location.assign(appendShortTermToken(annotation.url + '?draw=true&complete=true&increaseArea=1.25', this.shortTermToken), '_blank');
    },
    copyURL() {
      copyToClipboard(window.location.origin + '/#' + this.annotationURL);
      this.$notify({type: 'success', text: this.$t('notif-success-annot-URL-copied')});
    },
    // AC-12.4: hands a `SharePayload` ({kind, ref, deep_link, thumb}) to the chat module — this
    // panel never touches chat state directly, just emits on the shared event bus.
    shareInChat() {
      shareInChat(this.$eventBus, {
        kind: 'annotation',
        ref: `annotation:${this.annotation.id}`,
        deep_link: this.annotationURL,
        thumb: this.annotation.annotationCropURL(128, 'jpg'),
      });
    },

    async newTerm(term) { // a new term was added to the ontology
      this.$emit('addTerm', term);
      this.$store.dispatch('currentProject/fetchOntology');
      this.addTerm(term.id);
    },

    async addTerm(idTerm) {
      if (idTerm) {
        try {
          await new AnnotationTerm({annotation: this.annotation.id, term: idTerm}).save();
          this.$emit('updateTerms');
          this.showTermSelector = false;
        } catch (error) {
          this.$notify({type: 'error', text: this.$t('notif-error-add-term')});
          this.revTerms++;
        } finally {
          this.addTermString = '';
        }
      }
    },
    async removeTerm(idTerm, idUser) {
      idUser = idUser || this.findUserByTerm(idTerm); // if term removed from ontology tree, idUser not set => find it manually
      if (!idUser) {
        return;
      }

      try {
        await AnnotationTerm.delete(this.annotation.id, idTerm, Number(idUser));
        this.$emit('updateTerms');
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-remove-term')});
        this.revTerms++;
      }
    },
    findUserByTerm(idTerm) {
      if (this.annotation.userByTerm) {
        let match = this.annotation.userByTerm.find(ubt => ubt.term === idTerm);
        if (match) {
          return match.user[0];
        }
      }
    },

    async newTrack(track) {
      this.$emit('addTrack', track);
      this.addTrack(track.id);
    },
    async addTrack(idTrack) {
      if (idTrack) {
        try {
          await new AnnotationTrack({annotation: this.annotation.id, track: idTrack}).save();
          this.$emit('updateTracks');
          this.showTrackSelector = false;
        } catch (error) {
          this.$notify({type: 'error', text: this.$t('notif-error-add-track')});
          this.revTracks++;
        } finally {
          this.addTrackString = '';
        }
      }
    },
    async removeTrack(idTrack) {
      if (idTrack) {
        try {
          await AnnotationTrack.delete(this.annotation.id, idTrack);
          this.$emit('updateTracks');
        } catch (error) {
          this.$notify({type: 'error', text: this.$t('notif-error-remove-track')});
          this.revTracks++;
        } finally {
          this.addTrackString = '';
        }
      }
    },

    openCommentsModal() {
      this.$buefy.modal.open({
        parent: this,
        component: AnnotationCommentsModal,
        props: {annotation: this.annotation, comments: this.comments},
        hasModalCard: true,
        events: {'addComment': this.addComment}
      });
    },

    addComment(comment) {
      this.comments.unshift(comment);
    },

    openSpatialProfileModal() {
      this.openProfileModal(true);
    },

    openRegularProfileModal() {
      this.openProfileModal(false);
    },

    openProfileModal(spatialAxis) {
      this.$buefy.modal.open({
        parent: this,
        component: ProfileModal,
        props: {annotation: this.annotation, image: this.image, spatialAxis},
        hasModalCard: true
      });
    },

    confirmDeletion() {
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: this.$t('confirm-deletion-annotation'),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: () => this.deleteAnnot()
      });
    },

    async deleteAnnot() {
      try {
        await this.annotation.delete();
        this.$emit('deletion');
      } catch (err) {
        this.$notify({type: 'error', text: this.$t('notif-error-annotation-deletion')});
      }
    }
  },
  async created() {
    if (this.isPropDisplayed('comments') && this.annotation.type === AnnotationType.USER) {
      try {
        this.comments = (await AnnotationCommentCollection.fetchAll({annotation: this.annotation})).array;
        if (this.showComments) {
          this.openCommentsModal();
        }
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-fetch-annotation-comments')});
      }
    }

    try {
      this.properties = (await PropertyCollection.fetchAll({object: this.annotation})).array;
    } catch (error) {
      this.loadPropertiesError = true;
      console.log(error);
    }

    this.$eventBus.$emit('hide-similar-annotations');
  },
  destroyed() {
    this.$eventBus.$emit('hide-similar-annotations');
  },
};
</script>

<style scoped>
.annotation-details {
  display: flex;
  flex-direction: column;
  min-height: 0;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  color: #1c2230;
}

.num, .mono {
  font-family: 'IBM Plex Mono', 'SF Mono', ui-monospace, 'Roboto Mono', monospace;
}

/* ---- metrics ---- */
.ap-metrics {
  flex: 0 0 auto;
  border-bottom: 1px solid #eef0f4;
  background: #fbfbfd;
}
.ap-metric-row {
  display: flex;
  align-items: stretch;
}
.ap-metric-row-rect {
  border-top: 1px solid #eef0f4;
}
.ap-metric-row-rect.clickable {
  cursor: pointer;
}
.ap-metric-row-rect.clickable:hover {
  background: #f5f7fb;
}
.ap-metric {
  flex: 1;
  padding: 10px 14px;
}
.ap-metric-sep {
  width: 1px;
  background: #eef0f4;
  margin: 9px 0;
}
.ap-metric-label {
  font-size: 9px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a2a8b4;
  font-weight: 600;
  margin-bottom: 3px;
}
.ap-metric-val {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.ap-metric-val .num {
  font-size: 15px;
  font-weight: 600;
  color: #1c2230;
  letter-spacing: -0.01em;
}
.ap-metric-val .unit {
  font-size: 10px;
  color: #8a90a0;
  font-family: 'IBM Plex Mono', 'SF Mono', ui-monospace, monospace;
}
.ap-profile {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 14px 10px;
}
.ap-mini-btn {
  padding: 4px 9px;
  background: #fff;
  border: 1px solid #e6e8ef;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 500;
  color: #4a5160;
  cursor: pointer;
}
.ap-mini-btn:hover {
  border-color: #1e63d0;
  color: #1e63d0;
}

/* ---- terms (pinned) ---- */
.ap-terms {
  flex: 0 0 auto;
  padding: 9px 14px 10px;
  border-bottom: 1px solid #eef0f4;
  position: relative;
}
.ap-terms-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}
.ap-mini-label {
  font-size: 9px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a2a8b4;
  font-weight: 600;
  margin-right: 2px;
}
.ap-term-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 5px 2px 8px;
  background: #f1f3f7;
  border: 1px solid #e6e8ef;
  border-radius: 14px;
  font-size: 11.5px;
  font-weight: 500;
  color: #3a414f;
}
.ap-chip-x {
  width: 15px;
  height: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: #aab0bc;
  padding: 0;
}
.ap-chip-x:hover {
  background: #e2e5ec;
  color: #6e7585;
}
.ap-term-add {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px dashed #d2d6df;
  border-radius: 50%;
  color: #9aa0ad;
  cursor: pointer;
}
.ap-term-add:hover {
  border-color: #1e63d0;
  color: #1e63d0;
  background: rgba(30, 99, 208, 0.05);
}
.ap-empty {
  font-size: 12px;
  color: #aab0bc;
  font-style: italic;
}

/* ---- chip rail ---- */
.ap-rail {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 14px;
  border-bottom: 1px solid #eef0f4;
}
.ap-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 9px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  border: 1px solid #e8eaf0;
  background: #f6f7f9;
  color: #5a6170;
}
.ap-chip:hover {
  filter: brightness(0.97);
}
.ap-chip.on {
  border-color: #1e63d0;
  background: rgba(30, 99, 208, 0.10);
  color: #1750a8;
}
.ap-count {
  font-family: 'IBM Plex Mono', 'SF Mono', ui-monospace, monospace;
  font-size: 9.5px;
  font-weight: 600;
  padding: 0 5px;
  border-radius: 8px;
  min-width: 16px;
  text-align: center;
  background: rgba(20, 25, 50, 0.07);
}

/* ---- detail region ---- */
.ap-detail {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 42vh;
  overflow-y: auto;
  background: #fafbfc;
}
.ap-detail-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 4px;
}
.ap-detail-label {
  font-size: 9.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9aa0ad;
  font-weight: 700;
}
.ap-detail-line {
  flex: 1;
  height: 1px;
  background: #eceef3;
}
.ap-collapse {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 5px;
  cursor: pointer;
  color: #aab0bc;
}
.ap-collapse:hover {
  background: #eceef3;
  color: #6e7585;
}
.ap-detail-body {
  padding: 4px 14px 14px;
  font-size: 0.85rem;
}
.ap-search-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px;
  background: #fff;
  border: 1px dashed #d2d6df;
  border-radius: 7px;
  color: #5a6170;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}
.ap-search-btn:hover {
  color: #1e63d0;
  border-color: #1e63d0;
}

/* info rows */
.ap-info {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.ap-info-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ap-info-row .k {
  font-size: 10.5px;
  color: #a2a8b4;
  width: 72px;
  flex: 0 0 auto;
}
.ap-info-row .v {
  font-size: 12px;
  color: #3a414f;
}

/* term/track selector dropdowns (kept from the original) */
.add-term-wrapper, .add-track-wrapper {
  position: relative;
  margin-top: 7px;
}
.ontology-tree-container, .track-tree-container {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 500;
  padding-top: 5px;
  background: white;
  width: 100%;
  max-height: 30vh;
  overflow: auto;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
  border-radius: 4px;
  margin-top: 4px;
}

/* ---- footer ---- */
.ap-footer {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 11px 12px 12px;
  border-top: 1px solid #eef0f4;
  background: #fbfbfd;
}
.ap-primary {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: #1e63d0;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.ap-primary:hover {
  background: #1750a8;
  color: #fff;
}
.ap-ico {
  flex: 0 0 auto;
  width: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #f4f5f8;
  border: 1px solid #e8eaf0;
  border-radius: 8px;
  color: #4a5160;
  cursor: pointer;
}
.ap-ico.ap-ico-wide {
  width: auto;
  padding: 0 9px;
}
.ap-ico .num {
  font-size: 11px;
  font-weight: 600;
}
.ap-ico:hover {
  background: #eceef3;
  border-color: #dadde5;
  color: #1e63d0;
}
.ap-ico-danger {
  background: #fff;
  border-color: #f1d6d6;
  color: #e03131;
}
.ap-ico-danger:hover {
  background: #fdeeee;
  border-color: #e9bcbc;
  color: #e03131;
}

::v-deep .sl-vue-tree-node-item {
  font-size: 0.9em;
}
::v-deep .ap-terms .tag,
::v-deep .b-tag,
::v-deep .tag {
  margin-right: 5px;
  margin-bottom: 5px !important;
}
</style>
